import type {
	GraphNode,
	NodePosition,
	PositionsMap,
	LayoutResult
} from '../types';
import { zoneOrder, badgeSizes } from '../config';
import { getNodeSize, getNodeRadius, getNodeOrderHint, getNodeSpacing } from '../svg';
import { choosePrimaryParent } from '../graph';

// Tree-first layout constants from tree-tidy-layout.html
const marginLeft = 64;
const marginTop = 96;
const zoneGap = 56;
const sidePadding = 40;

export function computeTreeFirstLayout(nodes: Record<string, GraphNode>): LayoutResult {
	// Set up tree structure with primary parents
	Object.values(nodes).forEach((node) => {
		node.treeChildren = [];
	});

	Object.values(nodes).forEach((node) => {
		if (node.id === 'node1') return;
		const primaryParent = choosePrimaryParent(node, nodes);
		node.primaryParent = primaryParent;
		if (primaryParent) nodes[primaryParent].treeChildren!.push(node.id);
	});

	// Sort tree children
	Object.values(nodes).forEach((node) => {
		node.treeChildren!.sort(
			(leftId, rightId) =>
				getNodeOrderHint(nodes[leftId]) - getNodeOrderHint(nodes[rightId]) ||
				nodes[leftId].sourceIndex - nodes[rightId].sourceIndex
			);
	});

	// Compute subtree widths bottom-up - dynamically initialize for all zones
	const zoneRoots: Record<string, string[]> = {};
	zoneOrder.forEach((zone) => {
		zoneRoots[zone] = [];
	});
	nodes['node1'].treeChildren!.forEach((childId) => {
		const child = nodes[childId];
		if (zoneRoots[child.placementZone!]) zoneRoots[child.placementZone!].push(childId);
	});

	zoneOrder.forEach((zone) => {
		zoneRoots[zone].sort(
			(leftId, rightId) =>
				getNodeOrderHint(nodes[leftId]) - getNodeOrderHint(nodes[rightId]) ||
				nodes[leftId].sourceIndex - nodes[rightId].sourceIndex
		);
		zoneRoots[zone].forEach((rootId) => computeSubtreeWidth(rootId, nodes));
	});

	// Calculate dimensions
	const maxLevel = Math.max(...Object.values(nodes).map((node) => node.level));

	const zoneWidths: Record<string, number> = {};
	zoneOrder.forEach((zone) => {
		const forestWidth = computeForestWidth(zoneRoots[zone], nodes);
		zoneWidths[zone] = Math.max(280, Math.ceil(forestWidth + sidePadding * 2));
	});

	const zoneCenters: Record<string, number> = {};
	let currentX = marginLeft;
	zoneOrder.forEach((zone) => {
		zoneCenters[zone] = currentX + zoneWidths[zone] / 2;
		currentX += zoneWidths[zone] + zoneGap;
	});
	const totalWidth = currentX + marginLeft - zoneGap;

	// Calculate level Y positions with uniform spacing
	const levelMaxRadius: Record<number, number> = {};
	Object.values(nodes).forEach((node) => {
		levelMaxRadius[node.level] = Math.max(levelMaxRadius[node.level] || 0, getNodeRadius(node));
	});

	const globalMaxRadius = Math.max(...Object.values(levelMaxRadius));
	const uniformLevelStep = globalMaxRadius * 2 + 40;
	const levelY: Record<number, number> = { 0: marginTop };
	for (let level = 1; level <= maxLevel; level++) {
		levelY[level] = marginTop + uniformLevelStep * level;
	}
	const totalHeight = levelY[maxLevel] + globalMaxRadius + 96;

	// Place nodes
	const positions: PositionsMap = {
		node1: { x: totalWidth / 2, y: marginTop, node: nodes['node1'] }
	};

	zoneOrder.forEach((zone) => {
		const roots = zoneRoots[zone];
		if (!roots.length) return;
		const forestWidth = computeForestWidth(roots, nodes);
		let cursorX = zoneCenters[zone] - forestWidth / 2;
		roots.forEach((rootId, index) => {
			const rootWidth = nodes[rootId].subtreeWidth!;
			const rootCenter = cursorX + rootWidth / 2;
			placeSubtree(rootId, rootCenter, levelY, positions, nodes);
			cursorX += rootWidth + (index < roots.length - 1 ? 18 : 0);
		});
	});

	// Align root
	if (positions['node44']) {
		positions['node1'].x = positions['node44'].x;
	}

	// Straighten exclusive chains
	straightenExclusiveChains(positions, nodes);

	return {
		positions,
		zoneCenters,
		zoneWidths,
		levelY,
		maxLevel,
		totalWidth,
		totalHeight
	};
}

function computeSubtreeWidth(nodeId: string, nodes: Record<string, GraphNode>): number {
	const node = nodes[nodeId];
	const childIds = node.treeChildren!;

	if (!childIds.length) {
		node.subtreeWidth = Math.max(getNodeSize(node) + 16, 64);
		return node.subtreeWidth;
	}

	const childWidths = childIds.map((childId) => computeSubtreeWidth(childId, nodes));
	const childSpan = childWidths.reduce((acc, width) => acc + width, 0) + Math.max(0, childIds.length - 1) * 18;
	node.subtreeWidth = Math.max(getNodeSize(node) + 20, childSpan);
	return node.subtreeWidth;
}

function computeForestWidth(rootIds: string[], nodes: Record<string, GraphNode>): number {
	if (!rootIds.length) return 0;
	if (rootIds.length === 1) return nodes[rootIds[0]].subtreeWidth!;

	let width = getNodeRadius(nodes[rootIds[0]]) + getNodeRadius(nodes[rootIds[rootIds.length - 1]]);
	for (let index = 1; index < rootIds.length; index++) {
		width += getNodeSpacing(nodes[rootIds[index - 1]], nodes[rootIds[index]]);
	}
	return Math.max(width, rootIds.reduce((acc, id) => acc + nodes[id].subtreeWidth!, 0));
}

function placeSubtree(
	nodeId: string,
	centerX: number,
	levelY: Record<number, number>,
	positions: PositionsMap,
	nodes: Record<string, GraphNode>
): void {
	const node = nodes[nodeId];
	positions[nodeId] = { x: centerX, y: levelY[node.level], node };

	const childIds = node.treeChildren!;
	if (!childIds.length) return;

	const childWidths = childIds.map((childId) => nodes[childId].subtreeWidth!);
	const gaps = Math.max(0, childIds.length - 1) * 18;
	const totalChildWidth = childWidths.reduce((acc, width) => acc + width, 0) + gaps;

	let cursorX = centerX - totalChildWidth / 2;
	childIds.forEach((childId, index) => {
		const childWidth = childWidths[index];
		const childCenter = cursorX + childWidth / 2;
		placeSubtree(childId, childCenter, levelY, positions, nodes);
		cursorX += childWidth + 18;
	});
}

function straightenExclusiveChains(positions: PositionsMap, nodes: Record<string, GraphNode>): void {
	for (let pass = 0; pass < 3; pass++) {
		Object.values(nodes).forEach((node) => {
			if (node.id === 'node1' || !node.primaryParent) return;

			const parent = nodes[node.primaryParent];
			const previousLevelParents = node.parents.filter((parentId) => nodes[parentId].level === node.level - 1);
			const nextLevelChildren = parent.children.filter((childId) => nodes[childId].level === parent.level + 1);

			if (
				previousLevelParents.length === 1 &&
				previousLevelParents[0] === parent.id &&
				nextLevelChildren.length === 1 &&
				nextLevelChildren[0] === node.id
			) {
				if (!positions[node.id] || !positions[parent.id]) return;
				positions[node.id].x = positions[parent.id].x;
			}
		});
	}
}
